// ============================================================================
// FSA DOCUMENT OUTPUT CONFIGURATION — TypeScript types (ADR-0003)
// ============================================================================
// Mirrors the C# DocumentOutputConfig / DocumentOutputProfileDto. The persisted
// "house style" that drives the FSA PDF deliverable (header content, page size,
// font scale, footer, board signature preamble).

import type { ID } from '../common.types';

export type PageSizeKey = 'A4' | 'LEGAL' | 'LETTER';
export type FontScale = 'SMALL' | 'MEDIUM' | 'LARGE' | 'XLARGE';
export type HeaderAlign = 'LEFT' | 'CENTER' | 'RIGHT';
export type PageNumberPosition = 'LEFT' | 'CENTER' | 'RIGHT';
export type HeaderFieldKey = 'CLIENT_LEGAL_NAME' | 'ADDRESS' | 'FINANCIAL_YEAR' | 'REGISTRATION';
export type ColorMode = 'MONOCHROME' | 'BRAND';

export interface HeaderFieldConfig {
  key: HeaderFieldKey;
  /** For key = REGISTRATION — the basic.tbl_registration_type.code (CIN / GSTIN / …). */
  registrationTypeCode?: string | null;
  enabled: boolean;
}

export interface HeaderConfig {
  align: HeaderAlign;
  fields: HeaderFieldConfig[];
}

export interface PageConfig { size: PageSizeKey; }
export interface FontConfig { scale: FontScale; }
export interface PageNumberConfig { show: boolean; position: PageNumberPosition; }
export interface FooterConfig { customText?: string | null; pageNumber: PageNumberConfig; }
/** ADR-0003 Phase 1.5 — print palette. MONOCHROME is the statutory deliverable. */
export interface ColorConfig { mode: ColorMode; }

export interface DocumentOutputConfig {
  version: number;
  header: HeaderConfig;
  page: PageConfig;
  font: FontConfig;
  footer: FooterConfig;
  color: ColorConfig;
}

export interface DocumentOutputProfile {
  id: ID;
  name: string;
  description?: string | null;
  isDefault: boolean;
  config: DocumentOutputConfig;
  statusId?: string | null;
  statusCode?: string;
  statusName?: string;
  statusColor?: string | null;
  rowVersion: number;
}

export interface CreateDocumentOutputProfileRequest {
  name: string;
  description?: string | null;
  isDefault: boolean;
  config: DocumentOutputConfig;
  statusCode?: string;
}

export interface UpdateDocumentOutputProfileRequest {
  name: string;
  description?: string | null;
  isDefault: boolean;
  config: DocumentOutputConfig;
  statusCode?: string;
  rowVersion: number;
}

export interface RegistrationTypeOption { code: string; name: string; }

/** Mirrors C# DocumentOutputConfig.Default(). */
export const DEFAULT_OUTPUT_CONFIG: DocumentOutputConfig = {
  version: 1,
  header: {
    align: 'CENTER',
    fields: [
      { key: 'CLIENT_LEGAL_NAME', enabled: true },
      { key: 'ADDRESS', enabled: true },
      { key: 'FINANCIAL_YEAR', enabled: true },
    ],
  },
  page: { size: 'A4' },
  font: { scale: 'MEDIUM' },
  footer: { customText: null, pageNumber: { show: true, position: 'RIGHT' } },
  color: { mode: 'MONOCHROME' },
};

export const FONT_SCALE_LABELS: Record<FontScale, string> = {
  SMALL: 'Small', MEDIUM: 'Medium', LARGE: 'Large', XLARGE: 'Extra Large',
};

export const COLOR_MODE_LABELS: Record<ColorMode, string> = {
  MONOCHROME: 'Monochrome (final statement)',
  BRAND: 'Brand colour (drafts)',
};
